import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Menu, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { scrollToSection } from "@/lib/section-nav";

const NAV_LINKS = [
  { name: "Services", id: "services" },
  { name: "What We Build", id: "ideas" },
  { name: "Leadership", id: "leadership" },
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [location, navigate] = useLocation();
  const onHome = location === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // The nav targets are sections of the home page. When we're already on home,
  // smooth-scroll to them. From another route (e.g. a founder profile), do a
  // client-side navigation to `/#id`; Home's mount effect then scrolls to the
  // section. (A full-page navigation would race the browser's scroll
  // restoration, so we navigate within the SPA instead.)
  const goToSection = (e: React.MouseEvent<HTMLElement>, id: string) => {
    e.preventDefault();
    setIsOpen(false);
    if (onHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
      scrollToSection(id);
    }
  };

  const goHomeTop = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setIsOpen(false);
    if (onHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      window.scrollTo(0, 0);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 overflow-hidden ${
        isScrolled
          ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 flex items-center justify-between">
        <a
          href="/"
          aria-label="Pax Magellanic — home"
          className="flex items-center gap-3 group"
          onClick={goHomeTop}
        >
          <img
            src={`${import.meta.env.BASE_URL}assets/brand/mark-color.svg`}
            alt=""
            aria-hidden="true"
            className="h-9 w-9 md:h-10 md:w-10 select-none transition-transform duration-200 group-hover:scale-105"
            draggable={false}
          />
          <span
            className="text-base md:text-lg tracking-[0.06em] text-foreground select-none whitespace-nowrap"
            style={{ fontFamily: '"Major Mono Display", ui-monospace, Menlo, Consolas, monospace' }}
          >
            Pax Magellanic
          </span>
        </a>

        {/* Desktop Nav (lg+; below that we use the hamburger menu) */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.name}
              href={`/#${link.id}`}
              onClick={(e) => goToSection(e, link.id)}
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              {link.name}
            </a>
          ))}
          <Button asChild className="group">
            <a href="/#contact" onClick={(e) => goToSection(e, "contact")}>
              Start the Voyage
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </a>
          </Button>
        </nav>

        {/* Mobile + tablet Nav (below lg) */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" aria-label="Open Menu">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-background/95 backdrop-blur-xl border-l border-border flex flex-col pt-16">
            <nav className="flex flex-col gap-6 items-center mt-12">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.name}
                  href={`/#${link.id}`}
                  onClick={(e) => goToSection(e, link.id)}
                  className="text-2xl font-display font-medium text-foreground hover:text-primary transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <Button asChild size="lg" className="mt-8 w-full group">
                <a href="/#contact" onClick={(e) => goToSection(e, "contact")}>
                  Start the Voyage
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
